import type { Patient } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import { useEffect, useState, useMemo } from 'react';
import { Loader, Message, Card } from 'semantic-ui-react';
import useSWR from 'swr';
import type { PatientEntry } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import MemberCard from '../card';

import {
  normalizeSearchString,
  buildSearchParam,
  buildEligibilityParam,
  commonSWROptions,
  type PatientSearchResult,
} from './members-list-utils';

interface InactiveMembersProps {
  payer: string;
  searchString: string;
  selectedEligibilityGroup?: string | null;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  onPatientSelect: (patient: Patient) => void;
}

function InactiveMembers(props: InactiveMembersProps) {
  const authInfo = useAuthContext();
  const [patientGroups, setPatientGroups] = useState<PatientEntry[]>([]);

  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelect(selectedPatient);
  };

  const normalizedSearchString = useMemo(() => normalizeSearchString(props.searchString), [props.searchString]);

  const options = useMemo(
    () => ({
      ...commonSWROptions,
      onSuccess: (data: PatientSearchResult) => {
        const tempPatient: PatientEntry[] = [];
        //TODO: move this to fhir-proxy later
        //filter out the patient data that doesn't meet the criteria
        if (data && data?.entry?.length > 0) {
          for (let i = 0; i < data?.entry?.length; i++) {
            // In range by the loop bound; the assertion satisfies
            // `noUncheckedIndexedAccess` without touching the optional chain.
            const patient = data?.entry[i] as PatientEntry;
            const memberStatus = fhirpath.evaluate(
              patient,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/member-status').value"
            )?.[0];
            const patientEligibilityGroups = fhirpath.evaluate(
              patient,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/eligibility-group').value"
            );
            if (
              props?.selectedEligibilityGroup &&
              patientEligibilityGroups?.includes(props.selectedEligibilityGroup) &&
              memberStatus === 'INACTIVE'
            ) {
              tempPatient.push(patient);
            } else if (memberStatus === 'INACTIVE' && !props?.selectedEligibilityGroup) {
              tempPatient.push(patient);
            }
          }
        }
        setPatientGroups(tempPatient);
      },
    }),
    [props?.selectedEligibilityGroup]
  );

  const key = useMemo(() => {
    const searchParam = buildSearchParam(normalizedSearchString);
    const eligibilityParam = buildEligibilityParam(props.selectedEligibilityGroup);

    if (props.payer === 'ALL') {
      return `/Patient/?identifier=INACTIVE&_count=1000${searchParam}`;
    } else {
      return `/Patient/?identifier=https://projectwell.io/fhir/identifiers/member-status|INACTIVE&_count=1000&organization=${props.payer}${searchParam}${eligibilityParam}`;
    }
  }, [props.payer, normalizedSearchString, props.selectedEligibilityGroup]);

  const {
    data: _patientData,
    error: patientError,
    isValidating: patientsDataLoading,
    mutate: patientsDataMutate,
  } = useSWR<PatientSearchResult, ApiFetchError>([key, authInfo.token], FHIRFetcher, options);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
    patientsDataMutate();
  }, [props?.selectedEligibilityGroup, patientsDataMutate]);

  useEffect(() => {
    if (props.refresh === true) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      patientsDataMutate();
      props.setRefresh(false);
    }
  }, [props.refresh, patientsDataMutate, props.setRefresh]);

  return (
    <div>
      <p />

      {patientsDataLoading && <Loader active inline="centered" content="Loading inactive members..." />}

      {patientGroups.length > 0 && (
        <Card.Group>
          {patientGroups?.map((patientEntry) => {
            return (
              <MemberCard
                key={patientEntry.resource.id}
                patient={patientEntry.resource}
                onPatientSelect={handlePatientSelect}
              />
            );
          })}
        </Card.Group>
      )}

      {!patientsDataLoading && patientGroups && patientGroups.length === 0 && (
        <Message info>
          <Message.Header>No Inactive Members</Message.Header>
          <p>Looks like there are no Inactive Members</p>
        </Message>
      )}

      {patientError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(patientError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default InactiveMembers;
